(() => {
  const parseReviewData = (value) => {
    if (!value) return null;

    try {
      let data = JSON.parse(value);
      if (typeof data === 'string') data = JSON.parse(data);
      return data;
    } catch (error) {
      console.warn('Unable to read Judge.me review data.', error);
      return null;
    }
  };

  const getReviews = (data) => {
    const reviews = data?.reviews ?? data?.data?.reviews ?? data?.items;
    if (Array.isArray(reviews)) return reviews;
    if (Array.isArray(reviews?.reviews)) return reviews.reviews;
    return [];
  };

  const createElement = (tagName, className, text) => {
    const element = document.createElement(tagName);
    if (className) element.className = className;
    if (text != null) element.textContent = text;
    return element;
  };

  const getPlainText = (value) => {
    if (!value) return '';
    const parsed = new DOMParser().parseFromString(String(value), 'text/html');
    return (parsed.body.textContent || '').trim();
  };

  const createStars = (rating) => {
    const value = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
    const stars = createElement('div', 'custom-reviews-product__stars');
    stars.setAttribute('role', 'img');
    stars.setAttribute('aria-label', `${value} out of 5 stars`);

    for (let index = 1; index <= 5; index += 1) {
      const star = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      star.setAttribute('class', `custom-reviews-product__star${index > value ? ' is-empty' : ''}`);
      star.setAttribute('width', '12');
      star.setAttribute('height', '11');
      star.setAttribute('viewBox', '0 0 12 11');
      star.setAttribute('aria-hidden', 'true');

      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', 'M1.96389 10.816L3.35489 6.68197L0.000890661 4.43297H4.10889L5.57789 0L7.07289 4.43297H11.1549L7.80089 6.68197L9.21789 10.816L5.57789 8.17697L1.96389 10.816Z');
      path.setAttribute('fill', '#FFA500');
      star.append(path);
      stars.append(star);
    }

    return stars;
  };

  const isVerified = (review) => {
    const verifiedFlags = [
      review.verified,
      review.verified_buyer,
      review.reviewer_verified,
      review.is_verified,
      review.verified_purchase,
    ];
    const truthyValues = ['true', '1', 'yes', 'verified'];
    if (verifiedFlags.some((value) => value === true || truthyValues.includes(String(value).toLowerCase()))) return true;

    const statuses = [
      review.verified_status,
      review.verification_status,
      review.buyer_status,
      review.verified?.status,
    ].map((value) => String(value || '').toLowerCase());
    const verifiedStatuses = ['buyer', 'confirmed-buyer', 'verified-purchase', 'semi-verified-purchase', 'admin'];
    if (statuses.some((status) => verifiedStatuses.includes(status))) return true;

    const source = String(review.source || review.review_source || '').toLowerCase();
    return ['email', 'sms', 'email-request', 'email_requested', 'review-request', 'review_request'].includes(source);
  };

  const createVerifiedBadge = () => {
    const badge = createElement('span', 'custom-reviews-product__badge', 'Verified');
    const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    icon.setAttribute('viewBox', '0 0 12 12');
    icon.setAttribute('width', '12');
    icon.setAttribute('height', '12');
    icon.setAttribute('aria-hidden', 'true');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M2.2 6.2 4.8 8.5 9.8 3.7');
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', '#38AD59');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    path.setAttribute('stroke-width', '1.5');
    icon.prepend(path);
    badge.prepend(icon);
    return badge;
  };

  const createAvatarFallback = (name) => {
    const initials = String(name || '?')
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0])
      .join('')
      .toUpperCase() || '?';

    return createElement('span', 'custom-reviews-product__avatar custom-reviews-product__avatar--initials', initials);
  };

  const createAvatar = (review, name) => {
    const avatarValue = review.reviewer_avatar || review.reviewer_avatar_url || review.avatar_url || review.reviewer?.avatar_url;
    const avatarUrl = typeof avatarValue === 'string' ? avatarValue : avatarValue?.url;
    if (!avatarUrl) return createAvatarFallback(name);

    try {
      const parsedUrl = new URL(avatarUrl, window.location.origin);
      if (!['http:', 'https:'].includes(parsedUrl.protocol)) return createAvatarFallback(name);

      const image = createElement('img', 'custom-reviews-product__avatar');
      image.src = parsedUrl.href;
      image.alt = name ? `${name} avatar` : 'Reviewer avatar';
      image.width = 42;
      image.height = 42;
      image.loading = 'lazy';
      image.addEventListener('error', () => image.replaceWith(createAvatarFallback(name)), { once: true });
      return image;
    } catch (error) {
      return createAvatarFallback(name);
    }
  };

  const createReviewCard = (review) => {
    const card = createElement('li', 'custom-reviews-product__card');
    const body = createElement('div', 'custom-reviews-product__body');
    const meta = createElement('div', 'custom-reviews-product__meta');
    const identity = createElement('div', 'custom-reviews-product__identity');
    const name = review.reviewer_name || review.name || review.reviewer?.name || '';
    const reviewDate = review.created_at || review.createdAt || review.date;
    const rating = review.rating ?? review.rate;
    const content = getPlainText(review.body_html || review.body || review.content);

    if (name) identity.append(createElement('span', 'custom-reviews-product__name', name));
    if (isVerified(review)) identity.append(createVerifiedBadge());
    meta.append(identity);

    if (reviewDate) {
      const date = new Date(reviewDate);
      if (!Number.isNaN(date.getTime())) {
        const time = createElement(
          'time',
          'custom-reviews-product__date',
          new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date)
        );
        time.dateTime = date.toISOString().slice(0, 10);
        meta.append(time);
      }
    }

    body.append(meta);
    if (rating != null) body.append(createStars(rating));
    if (content) body.append(createElement('div', 'custom-reviews-product__review rte', content));
    card.append(createAvatar(review, name));
    card.append(body);
    return card;
  };

  const renderJudgeMeReviews = (root) => {
    if (root.dataset.reviewSource !== 'app') return;
    const list = root.querySelector('.custom-reviews-product__list');
    if (!list || list.dataset.loaded === 'true') return;

    list.dataset.loaded = 'true';
    const requestedLimit = Number.parseInt(root.dataset.reviewLimit, 10);
    const limit = Number.isFinite(requestedLimit) && requestedLimit > 0 ? requestedLimit : 3;
    const reviews = getReviews(parseReviewData(root.dataset.reviewData)).slice(0, limit);
    if (!reviews.length) {
      root.hidden = true;
      return;
    }

    list.replaceChildren(...reviews.map(createReviewCard));
  };

  const initialize = (scope = document) => {
    if (scope.matches?.('.custom-reviews-product[data-review-source="app"]')) {
      renderJudgeMeReviews(scope);
    }
    scope.querySelectorAll?.('.custom-reviews-product[data-review-source="app"]').forEach(renderJudgeMeReviews);
  };

  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
